import { roundToStrike } from "@/lib/options/structure";
import { layerColor } from "@/lib/palette";
import type { OptionLeg } from "@/types/options";
import type { StrategyLayer } from "@/types/strategy";
import {
  applyLegDeltas,
  applyLegOverrides,
  effectiveSpot,
  filterDisabledLegs,
} from "./leg-deltas";
import { getRegistryEntry } from "./registry";

/**
 * Resolve a layer to its canonical leg list — pivot, per-leg deltas,
 * type/side overrides, and disabled-leg filters all baked into the output.
 */
function resolveLayerLegs(
  layer: StrategyLayer,
  entrySpot: number,
  strikeStep?: number,
): OptionLeg[] {
  const entry = getRegistryEntry(layer.kind);
  const layerSpot = effectiveSpot(entrySpot, layer.params);
  return filterDisabledLegs(
    applyLegOverrides(
      applyLegDeltas(
        entry.toLegs(layerSpot, layer.params, strikeStep),
        layer.params,
      ),
      layer.params,
    ),
    layer.params,
  );
}

/** Split a quantity-N leg into N quantity-1 legs. */
function expandQuantity(legs: OptionLeg[]): OptionLeg[] {
  const out: OptionLeg[] = [];
  for (const leg of legs) {
    const q = Math.max(1, Math.floor(leg.quantity));
    for (let i = 0; i < q; i++) out.push({ ...leg, quantity: 1 });
  }
  return out;
}

/**
 * Encode a flat list of qty-1 legs as Custom-layer params, with each leg's
 * absolute strike represented as a `d_leg_N` delta from `anchorSpot`. The
 * anchor itself is NOT persisted into params — it's the runtime entrySpot
 * the Custom kind's `toLegs` will receive when the layer is later evaluated.
 * Type/side mismatches against the default (long call) get t_leg_N /
 * s_leg_N overrides.
 */
export function encodeLegsAsCustomParams(
  legs: OptionLeg[],
  anchorSpot: number,
  strikeStep?: number,
): Record<string, number> {
  const baseStrike = roundToStrike(anchorSpot, strikeStep);
  const params: Record<string, number> = { leg_count: legs.length };
  legs.forEach((leg, i) => {
    const delta = leg.strike - baseStrike;
    if (delta !== 0) params[`d_leg_${i}`] = delta;
    if (leg.type === "put") params[`t_leg_${i}`] = 1;
    if (leg.side === "short") params[`s_leg_${i}`] = -1;
  });
  return params;
}

/**
 * Combine all enabled layers into a single Custom layer.
 *
 * Walks each enabled source layer through the engine pipeline so pivot,
 * per-leg overrides, and disabled legs are baked into absolute strikes
 * before encoding. Quantity > 1 legs expand into duplicate qty-1 legs so
 * the destination Custom layer (whose legs are always qty-1) preserves
 * payoff. The resulting Custom layer encodes its legs as `d_leg_N` deltas
 * relative to `entrySpot`, so when later evaluated against a different
 * entrySpot it drifts the same way other layers do.
 * Returns null when no enabled layer contributes any legs.
 */
export function flattenLayersToCustom(
  layers: StrategyLayer[],
  newId: string,
  entrySpot: number,
  strikeStep?: number,
): StrategyLayer | null {
  const enabled = layers.filter((l) => l.enabled);
  if (enabled.length === 0) return null;

  const allLegs = enabled.flatMap((l) =>
    resolveLayerLegs(l, entrySpot, strikeStep),
  );
  const expanded = expandQuantity(allLegs);
  if (expanded.length === 0) return null;

  return {
    id: newId,
    kind: "custom",
    params: encodeLegsAsCustomParams(expanded, entrySpot, strikeStep),
    color: enabled[0].color,
    enabled: true,
  };
}

/**
 * Inverse of {@link flattenLayersToCustom}. Resolves each enabled layer's
 * legs (pivot, deltas, overrides, disabled-leg filters baked in), expands
 * quantity > 1 into duplicate qty-1 legs, then emits one Custom layer per
 * resolved leg. Each new layer gets a fresh palette color so they read as
 * independent in the simulator. `idGen` lets the caller stamp ids (real
 * usage passes `crypto.randomUUID`; tests can pass a counter).
 */
export function splitLayersToCustomLegs(
  layers: StrategyLayer[],
  idGen: () => string,
  entrySpot: number,
  strikeStep?: number,
): StrategyLayer[] {
  const enabled = layers.filter((l) => l.enabled);
  if (enabled.length === 0) return [];

  const allLegs = enabled.flatMap((l) =>
    resolveLayerLegs(l, entrySpot, strikeStep),
  );
  const expanded: OptionLeg[] = [];
  for (const leg of allLegs) {
    const q = Math.max(1, Math.floor(leg.quantity));
    for (let i = 0; i < q; i++) expanded.push({ ...leg, quantity: 1 });
  }
  if (expanded.length === 0) return [];

  return expanded.map((leg, i) => ({
    id: idGen(),
    kind: "custom",
    params: encodeLegsAsCustomParams([leg], entrySpot, strikeStep),
    color: layerColor(i),
    enabled: true,
  }));
}
